import { useCallback, useEffect, useState } from 'react'
import { IconShoppingBag, IconArrowLeft } from '@tabler/icons-react'
import classNames from 'classnames'

import { Button } from '~/components/Button'
import { Drawer } from '~/components/Drawer'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { useCartDrawer, useCustomerListDetail } from '~/hooks'
import styles from './CustomerListsDrawerDrawer.module.css'
import { CustomerListsDrawerProps } from './CustomerListsDrawerDrawer.types'
import { ListItem } from './ListItem'
import { ProductList } from './ProductList'

export const CustomerListsDrawer = ({
  open,
  onClose,
  lists,
  selectedList,
  setSelectedList,
  getLists,
}: CustomerListsDrawerProps) => {
  const [loading, setLoading] = useState(false)
  const { addCustomerListToCart } = useCustomerListDetail(selectedList)
  const { openCartDrawer } = useCartDrawer()

  const addListToCartHandler = useCallback(async () => {
    try {
      setLoading(true)
      const isSuccess = await addCustomerListToCart(selectedList?.id)
      if (isSuccess) {
        onClose()
        openCartDrawer()
        setSelectedList(null)
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err)
    } finally {
      setLoading(false)
    }
  }, [selectedList?.id, addCustomerListToCart, onClose, openCartDrawer])

  useEffect(() => {
    ;(async () => {
      getLists()
    })()
  }, [getLists])

  return (
    <Drawer
      width={506}
      wrapperClass={classNames(styles.wrapper)}
      theme={{ title: 'w-full' }}
      title={
        <div className='flex'>
          {selectedList && (
            <button
              className='text-primary-heading'
              type='button'
              onClick={() => {
                return setSelectedList(null)
              }}>
              <IconArrowLeft
                size={32}
                strokeWidth={2}
                className='inline-block'
              />
            </button>
          )}
          <Typography
            variant={TypographyVariant.BodyXLargeBold}
            tag={TypographyTag.span}
            className={classNames('flex-1', {
              'text-center': !!selectedList,
            })}>
            {selectedList ? selectedList.name || '' : 'Lists'}
          </Typography>
        </div>
      }
      isOpen={open}
      placement='right'
      onClose={() => {
        onClose()
        setSelectedList(null)
      }}>
      <div
        className={classNames(
          'flex-1 w-full py-6 overflow-y-auto px-6 md:px-8'
        )}>
        {!selectedList && (
          <>
            <div className='mb-4'>
              <Typography
                tag={TypographyTag.p}
                variant={TypographyVariant.Heading6}
                className={styles.heading}>
                My Lists ({lists?.length || 0})
              </Typography>
            </div>
            <div className='flex flex-col gap-6'>
              {lists?.map((list, index) => {
                return (
                  <ListItem
                    list={list}
                    key={index}
                    onClickDetail={() => {
                      setSelectedList(list)
                    }}
                  />
                )
              })}
            </div>
          </>
        )}
        {!!selectedList && (
          <ProductList
            list={selectedList}
            productIds={selectedList?.lineItems?.map(({ productId }) => {
              return productId
            })}
          />
        )}
      </div>
      <div
        className={classNames(
          'flex items-center justify-center w-full px-6 py-4 border-t gap-x-4'
        )}>
        <Button
          variant='outline'
          href={selectedList?.id ? `/lists/${selectedList?.id}` : '/lists'}
          className={classNames(
            styles.buttonCls,
            'py-4 flex w-full justify-center'
          )}
          onClick={() => {
            return setSelectedList(null)
          }}>
          <Typography
            variant={TypographyVariant.BodyLargeBold}
            tag={TypographyTag.span}>
            {!selectedList ? 'Manage lists' : 'Manage list'}
          </Typography>
        </Button>
        {!!selectedList && (
          <Button
            variant='solid'
            icon={<IconShoppingBag />}
            style={{ '--btn-icon-spacing': '12px' }}
            disabled={!selectedList?.lineItems?.length}
            loading={loading}
            className={classNames(
              styles.buttonCls,
              'py-4 flex w-full justify-center'
            )}
            onClick={addListToCartHandler}>
            <Typography
              variant={TypographyVariant.BodyLargeBold}
              tag={TypographyTag.span}>
              Add all to cart
            </Typography>
          </Button>
        )}
      </div>
    </Drawer>
  )
}
